import { ElementDefinition, LayoutOptions, StylesheetJson } from "cytoscape";
import { useMemo } from "react";

import { useTheme } from "src/theme/ThemeProvider";
import { Case } from "src/types/case";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import { AlgorithmViewType, RelatedWidgetConfiguration } from "../relatedCases.typings";

import useColaMapping from "./colaLayout/cola.mapping";
import colaOptions from "./colaLayout/cola.options";
import { colaStyles } from "./colaLayout/cola.styles";
import { getOldLayout } from "./oldLayout/old.styles";

type GetRelatedCasesGraphDataInput = {
  algorithm: AlgorithmViewType;
  mergeRelatedCases: boolean;
  caseInfo: {
    caseId?: string;
    alertId: string;
    propertyValues: PropertyValue[];
    elements: Element[];
  };
  relatedCases: Case[];
  configuration: RelatedWidgetConfiguration;
};

// Gets the cytoscape data, layout and styles based on the selected algorithm
export const useBuildGraphForLayout = ({
  algorithm,
  caseInfo,
  mergeRelatedCases,
  relatedCases,
  configuration,
}: GetRelatedCasesGraphDataInput): {
  graphData: ElementDefinition[];
  graphLayout: LayoutOptions;
  graphStyles: StylesheetJson;
} => {
  const { colors } = useTheme();

  const data = useColaMapping({
    elementsConfig: configuration.matchElementsColumns,
    caseInfo,
    options: {
      filteredIdentifiers: [],
      mergeRelatedCases,
    },
    relatedCases,
  });

  const memoizedData = useMemo(() => {
    return data;
    // don't include data in the dependencies array to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseInfo.caseId, configuration.matchElementsColumns, mergeRelatedCases, relatedCases]);

  const { graphLayout, graphStyles } = useMemo(() => {
    return {
      graphLayout: algorithm === "New" ? colaOptions : getOldLayout("elk"),
      graphStyles: colaStyles(colors),
    };
  }, [algorithm, colors]);

  return {
    graphData: memoizedData,
    graphLayout,
    graphStyles: graphStyles as StylesheetJson,
  };
};
